import {
    ChangeDetectionStrategy,
    Component,
    Injectable,
    ViewEncapsulation,
} from '@angular/core';
import {
    CredentialStoreGenericType,
    CredentialStoreResourceType,
    ICredentialStore,
    ODataResponse,
} from '@models';
import { AssetCredentialStoreResourcesSource } from '@pages/credential-stores/grid/asset-credential-store-resources.grid';
import { RobotCredentialStoreResourcesSource } from '@pages/credential-stores/grid/robot-credential-store-resources.grid';
import { CredentialStoreDeleteModalComponent } from '@pages/credential-stores/modal/credential-store-delete/credential-store-delete.modal';
import { CredentialStoreModalComponent } from '@pages/credential-stores/modal/credential-store/credential-store.modal';
import {
    GRID_SERVICE,
    IGridSource,
    ODataGridComponent,
} from '@pages/generics/grid';
import {
    CredentialStoresService,
    ModalService,
    ODataQueryOptions,
} from '@services';
import { filterNullOrUndefined } from '@utils/rxjs.utils';

import {
    BehaviorSubject,
    forkJoin,
} from 'rxjs';
import {
    filter,
    finalize,
    map,
    switchMap,
    switchMapTo,
    takeUntil,
} from 'rxjs/operators';

interface ICredentialStoreWithDefaults extends ICredentialStore {
    isRobotDefault: boolean;
    isAssetDefault: boolean;
}

@Injectable()
export class CredentialStoresSource implements IGridSource<ODataQueryOptions, ODataResponse<ICredentialStoreWithDefaults>> {
    public get mapper() {
        return this._credentialStore.mapper;
    }

    constructor(
        private _credentialStore: CredentialStoresService,
    ) { }

    public get(qo: ODataQueryOptions) {
        return forkJoin({
            credentialStores: this._credentialStore.get(qo),
            robotsDefaultStoreId: this._credentialStore.getDefaultStore(CredentialStoreResourceType.RobotCredential),
            assetsDefaultStoreId: this._credentialStore.getDefaultStore(CredentialStoreResourceType.AssetCredential),
        })
            .pipe(
                map(({ credentialStores, robotsDefaultStoreId, assetsDefaultStoreId }) => {
                    credentialStores.data = credentialStores.data
                        .map(credentialStore => ({
                            ...credentialStore,
                            isRobotDefault: robotsDefaultStoreId === credentialStore.id,
                            isAssetDefault: assetsDefaultStoreId === credentialStore.id,
                        }));

                    return credentialStores as ODataResponse<ICredentialStoreWithDefaults>;
                }),
            );
    }
}

@Component({
    templateUrl: './credential-stores.page.html',
    providers: [{
        provide: GRID_SERVICE,
        useClass: CredentialStoresSource,
        deps: [
            CredentialStoresService,
        ],
    }],
    styleUrls: [
        'credential-stores.page.scss',
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class CredentialStoresComponent extends ODataGridComponent<ICredentialStoreWithDefaults, number> {
    public CredentialStoreGenericType = CredentialStoreGenericType;
    public CredentialStoreResourceType = CredentialStoreResourceType;

    public loadingModal$ = new BehaviorSubject(false);

    private _modalService = this._injector.get<ModalService>(ModalService);
    private _credentialStoreService = this._injector.get<CredentialStoresService>(CredentialStoresService);
    private _assetCredentialStoreResourcesSource =
        this._injector.get<AssetCredentialStoreResourcesSource>(AssetCredentialStoreResourcesSource);
    private _robotCredentialStoreResourcesSource =
        this._injector.get<RobotCredentialStoreResourcesSource>(RobotCredentialStoreResourcesSource);

    public openModal(model?: ICredentialStore) {
        this.loadingModal$.next(true);

        const openModal = (credentialStore: ICredentialStore | null) =>
            this._modalService
                .openModal(CredentialStoreModalComponent, credentialStore)
                .afterClosed();

        (model ?
            this._credentialStoreService
                .getInstance(model.id)
                .pipe(
                    switchMap(openModal),
                ) :
            openModal(null)
        ).pipe(
            finalize(() => this.loadingModal$.next(false)),
            filterNullOrUndefined(),
            takeUntil(this._destroyed$),
        ).subscribe(
            () => this._refresh$.next(),
        );
    }

    public setDefaultStore(entry: ICredentialStore, resourceType: CredentialStoreResourceType) {
        this.loading$.next(true);

        this._credentialStoreService.setDefaultStore(entry, resourceType)
            .pipe(
                takeUntil(this._destroyed$),
            )
            .subscribe(
                () => this._refresh$.next(),
                finalize(() => this.loading$.next(false)),
            );
    }

    public remove(entry: ICredentialStore) {
        this._assetCredentialStoreResourcesSource.credentialStore = entry;
        this._robotCredentialStoreResourcesSource.credentialStore = entry;

        forkJoin({
            assets: this._assetCredentialStoreResourcesSource.get({
                top: 1,
            }),
            robots: this._robotCredentialStoreResourcesSource.get({
                top: 1,
            }),
        }).pipe(
            switchMap(
                result => result.robots.total + result.assets.total ?
                    this._modalService
                        .openModal(CredentialStoreDeleteModalComponent, entry, {
                            data: {
                                robotCount: result.robots.total,
                                assetCount: result.assets.total,
                            },
                        })
                        .afterClosed() :
                    this._modal.onConfirm(null,
                        undefined,
                        undefined,
                        this._translate.instant('credential_stores_action_remove_confirmation'),
                    ).pipe(filter(Boolean)),
            ),
        ).pipe(
            filterNullOrUndefined(),
            switchMapTo(
                this._credentialStoreService.deleteCredentialStore(entry),
            ),
        ).subscribe(
            () => this._refresh$.next(),
        );
    }
}
